import React, { useState } from "react";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend
} from "recharts";
import { useTranslation } from "react-i18next";

// Get accurate ISO week number
const getISOWeek = (date) => {
  const target = new Date(date.valueOf());
  const dayNumber = (date.getDay() + 6) % 7;
  target.setDate(target.getDate() - dayNumber + 3);
  const firstThursday = new Date(target.getFullYear(), 0, 4);
  const diff = target - firstThursday;
  return 1 + Math.round(diff / (7 * 24 * 60 * 60 * 1000));
};

const getFormattedLabel = (date, grouping) => {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, "0");
  const day = date.getDate().toString().padStart(2, "0");

  switch (grouping) {
    case "day":
      return `${day}/${month}/${year.toString().slice(-2)}`;
    case "week":
      return `W${getISOWeek(date)} ${year.toString().slice(-2)}`;
    case "month":
      return `${month}/${year.toString().slice(-2)}`;
    case "year":
      return `${year}`;
    default:
      return `${day}/${month}/${year.toString().slice(-2)}`;
  }
};

const GamesOverTimeChart = ({ gameInfos }) => {
  const { t } = useTranslation();
  const [groupBy, setGroupBy] = useState("day");

  const dateCounts = {};

  gameInfos.forEach((game) => {
    if (!game.startGameTime) return;

    const date = new Date(game.startGameTime);
    if (isNaN(date)) return;

    const label = getFormattedLabel(date, groupBy);
    dateCounts[label] = (dateCounts[label] || 0) + 1;
  });

  const chartData = Object.entries(dateCounts)
    .map(([label, count]) => ({ date: label, count }))
    .sort((a, b) => {
      // Year-based sort
      const extractSortKey = (label) => {
        const parts = label.match(/\d+/g);
        return parts ? parts.map(Number) : [0];
      };
      const aKey = extractSortKey(a.date);
      const bKey = extractSortKey(b.date);
      for (let i = 0; i < Math.max(aKey.length, bKey.length); i++) {
        const diff = (aKey[i] || 0) - (bKey[i] || 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });

  const chartTitles = {
    day: t("chart.gamesPerDay"),
    week: t("chart.gamesPerWeek"),
    month: t("chart.gamesPerMonth"),
    year: t("chart.gamesPerYear"),
  };

  return (
    <div
      style={{
        border: "1px solid #ccc",
        borderRadius: "8px",
        padding: "16px",
        marginBottom: "24px",
        backgroundColor: "#fff",
        boxShadow: "0 2px 5px rgba(0,0,0,0.05)",
        width: "100%",
        maxWidth: "900px",
        margin: "0 auto",
        boxSizing: "border-box",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h3 style={{
          fontSize: "1.25rem",
          fontWeight: "bold",
        }}>
          {chartTitles[groupBy]}
        </h3>
        <select
          value={groupBy}
          onChange={(e) => setGroupBy(e.target.value)}
          style={{
            padding: "4px 8px",
            fontSize: "1rem",
            borderRadius: "4px",
            border: "1px solid #ccc"
          }}
        >
          <option value="day">{t("chart.groupByDay")}</option>
          <option value="week">{t("chart.groupByWeek")}</option>
          <option value="month">{t("chart.groupByMonth")}</option>
          <option value="year">{t("chart.groupByYear")}</option>
        </select>
      </div>

      <div style={{ width: "100%", height: window.innerWidth < 600 ? "250px" : "320px" }}>
        <ResponsiveContainer>
          <LineChart data={chartData}>
            <XAxis dataKey="date" />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Legend />
            <Line type="monotone" dataKey="count" stroke="#8884d8" name={t("chart.games")} />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default GamesOverTimeChart;
